// src/hooks/useActivityInsights.js
import { useState, useEffect } from "react";
import { dashboardService } from "../services/dashboardService";
import { formatLocalDate } from "../utils/dateHelpers";

export const useActivityInsights = (dateRange) => {
  const [data, setData] = useState({
    jobPostings: [],
    interviewSchedules: [],
    perOrgResumes: [],
    availableOrgs: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);

      try {
        // Calculate date range with defaults
        const today = new Date();
        const defaultStart = new Date();
        defaultStart.setDate(today.getDate() - 7);
        
        const start = dateRange[0] ? new Date(dateRange[0]) : defaultStart;
        const end = dateRange[1] ? new Date(dateRange[1]) : today;
        
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        // Build params
        const params = new URLSearchParams();
        params.append("start_date", formatLocalDate(start));
        params.append("end_date", formatLocalDate(end));
        params.append("organization_name", "All Organizations");

        // Fetch data using service
        const response = await dashboardService.getActivityInsights(params);

        // Extract unique organizations
        const orgSet = new Set();
        response.job_descriptions?.forEach((j) => 
          j.OrganizationName && orgSet.add(j.OrganizationName)
        );
        response.interview_schedules?.results?.forEach((i) => 
          i.OrganizationName && orgSet.add(i.OrganizationName)
        );
        response.resume_parsed?.per_org?.results?.forEach((r) => 
          r.OrganizationName && orgSet.add(r.OrganizationName)
        );

        const availableOrgs = Array.from(orgSet).sort((a, b) => 
          a.localeCompare(b)
        );

        // Set all data
        setData({
          jobPostings: response.job_descriptions || [],
          interviewSchedules: response.interview_schedules?.results || [],
          perOrgResumes: response.resume_parsed?.per_org?.results || [],
          availableOrgs,
        });
      } catch (err) {
        console.error("Error fetching activity insights data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [dateRange]);

  return { ...data, loading, error };
};